import DOMPurify from "dompurify";

// Hosts permitted as <iframe> embeds in editorial / research / policy content.
// Blog and research bodies legitimately embed videos and doc viewers (the rendered
// content is even styled for iframes, e.g. BlogPost's `[&_iframe]` aspect-video rules).
// We allow iframes only from this allowlist of video/doc hosts and strip every other
// iframe — this keeps real embeds working while blocking arbitrary frames
// (clickjacking / phishing / javascript: frames).
const ALLOWED_IFRAME_HOSTS = new Set<string>([
  "www.youtube.com",
  "youtube.com",
  "www.youtube-nocookie.com",
  "youtube-nocookie.com",
  "player.vimeo.com",
  "vimeo.com",
  "www.loom.com",
  "loom.com",
  "docs.google.com",
  "drive.google.com",
]);

let hookInstalled = false;
function installIframeHook() {
  if (hookInstalled) return;
  DOMPurify.addHook("uponSanitizeElement", (node, data) => {
    if (data.tagName !== "iframe") return;
    const el = node as Element;
    let host = "";
    try {
      host = new URL(el.getAttribute("src") ?? "").host;
    } catch {
      host = "";
    }
    if (!ALLOWED_IFRAME_HOSTS.has(host)) {
      // Re-parent the rejected frame into a throwaway fragment instead of detaching it
      // outright. The node still leaves the sanitised tree, exactly as before; the only
      // difference is that it keeps A parent.
      //
      // WHY, MEASURED 2026-08-18. DOMPurify can decide to remove this same node again later
      // in the same walk: its SAFE_FOR_XML rule fires on an iframe whose RAWTEXT content
      // contains a "<". DOMPurify 3.4.11's hardened _forceRemove THROWS when asked to remove
      // a node that has no parent, so the plain removeChild above turned that second removal
      // into an exception that escaped sanitizeHtml into React's render phase. There is no
      // ErrorBoundary anywhere in this app, so the whole route unmounted to a blank page.
      // The trigger is not exotic: it is the shape the rich-text editor produces when it
      // wraps a src URL in an anchor, which is the corruption BlogPost's own repair regex
      // exists to undo, and which InsightsPost does not repair at all.
      //   <iframe src="<a href="https://x">https://x</a>"></iframe>
      // Pinned by tests/components/blog/sanitize-html-payload-battery.test.ts (payload
      // structural/editor/anchor-wrapped-src) and by the rendered-page suite beside it.
      el.ownerDocument.createDocumentFragment().appendChild(el);
    }
  });
  hookInstalled = true;
}

/**
 * Sanitize DB-sourced HTML before rendering via `dangerouslySetInnerHTML`.
 *
 * Removes scripts, event-handler attributes and other XSS vectors, but preserves
 * common safe embeds: <iframe> elements are kept only when their `src` host is on
 * ALLOWED_IFRAME_HOSTS, otherwise the iframe is dropped.
 *
 * Use this everywhere untrusted/stored HTML is injected into the DOM.
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
  installIframeHook();
  return DOMPurify.sanitize(dirty ?? "", {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: [
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "loading",
      "referrerpolicy",
    ],
  });
}
